import { tr } from '@/lib/i18n-core';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, get, post, qs } from '@/lib/api';
import { supervisorHeaders } from '../accounts/SupervisorApproval';
import { describeError } from '@/lib/auth';
import { dateTime, humanize, money, newIdempotencyKey } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable, Dialog, SearchFilterBar } from '@/components/ui/layout';
import {
  Alert, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, PasswordInput, StatusBadge, Textarea,
} from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

const SEARCH_PAGE_SIZE = 20;
const MIN_SEARCH_LENGTH = 2;
const QUANTITY_TOLERANCE = 1e-6;
const NOTES_MAX_LENGTH = 600;

const RETURN_REASONS = [
  { value: 'wrong_item', label: 'Wrong item dispensed', restocked: true },
  { value: 'patient_refused', label: 'Customer refused it', restocked: true },
  { value: 'damaged', label: 'Damaged', restocked: false },
  { value: 'expired', label: 'Expired', restocked: false },
  { value: 'other', label: 'Other', restocked: false },
] as const;

type ReturnReason = (typeof RETURN_REASONS)[number]['value'];

interface SaleSearchRow {
  id: string;
  number: string;
  status: string;
  createdAt: string;
  patient: { fullName: string; mrn: string } | null;
  walkInName: string | null;
  totals: { netMinor: number };
}

interface SaleSearchPage {
  data: SaleSearchRow[];
  nextCursor: string | null;
}

interface SaleLine {
  id: string;
  description: string;
  qty: number;
  returnedQty: number;
  unitPriceMinor: number;
  netMinor: number;
}

interface Sale {
  id: string;
  number: string;
  context: string;
  status: string;
  createdAt: string;
  walkInName: string | null;
  patient: { fullName: string; mrn: string } | null;
  everClaimed: boolean;
  items: SaleLine[];
}

interface SupervisorOverride {
  username: string;
  password: string;
}

interface ReturnBody {
  invoiceId: string;
  lines: { invoiceItemId: string; qty: number }[];
  reason: ReturnReason;
  notes?: string;
  supervisorOverride?: SupervisorOverride;
}

interface ReturnOutcome {
  creditMinor: number;
  lines: number;
  refundedMinor: number;
  refunds: { paymentId: string; mode: string; amountMinor: number }[];
  fullyReturned: boolean;
}

function formatQuantity(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

function returnableQty(line: SaleLine): number {
  const left = line.qty - line.returnedQty;
  return left > QUANTITY_TOLERANCE ? left : 0;
}

function parseQuantity(text: string | undefined): number {
  const value = Number(text);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function quantityProblem(line: SaleLine, text: string | undefined): string | null {
  if (!text || text.trim() === '') return null;
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0) return 'Enter zero or more';
  if (value > returnableQty(line) + QUANTITY_TOLERANCE) return `At most ${formatQuantity(returnableQty(line))}`;
  return null;
}

function creditFor(line: SaleLine, returningNow: number): number {
  const returnedAfter = Math.min(line.qty, line.returnedQty + returningNow);
  return Math.round((line.netMinor * returnedAfter) / line.qty) - Math.round((line.netMinor * line.returnedQty) / line.qty);
}

function customerOf(sale: { patient: { fullName: string; mrn: string } | null; walkInName: string | null }): string {
  return sale.patient ? `${sale.patient.fullName} (${sale.patient.mrn})` : sale.walkInName ?? 'Walk-in customer';
}

function blockedReason(sale: Sale): string | null {
  if (sale.context !== 'pharmacy') return 'This is not a pharmacy sale, so it cannot be returned here.';
  if (sale.status === 'cancelled') return tr('pharmacy', 'sale_cancelled_cannot_return');
  if (sale.status === 'draft') return tr('pharmacy', 'sale_estimate_cannot_return');
  if (sale.everClaimed) return 'This sale is on an insurance claim. Returns after claiming are settled with the payer.';
  if (sale.items.every((line) => returnableQty(line) === 0)) return 'Everything on this sale has already been returned.';
  return null;
}

function needsSupervisor(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && /supervisor/i.test(error.message);
}

function refundedByMode(outcome: ReturnOutcome): { mode: string; amountMinor: number }[] {
  const totals = new Map<string, number>();
  for (const refund of outcome.refunds) {
    totals.set(refund.mode, (totals.get(refund.mode) ?? 0) + refund.amountMinor);
  }
  return [...totals].map(([mode, amountMinor]) => ({ mode, amountMinor }));
}

function OutcomeSummary({ outcome }: { outcome: ReturnOutcome }) {
  const perMode = refundedByMode(outcome);
  return (
    <Alert
      variant="success"
      title={outcome.fullyReturned ? tr('pharmacy', 'return_recorded_the_whole_sale_has_now_been') : tr('pharmacy', 'return_recorded')}
    >
      <p>
        {tr('pharmacy', 'credit_of')} {money(outcome.creditMinor)} {tr('pharmacy', 'for_2')} {outcome.lines} {outcome.lines === 1 ? tr('pharmacy', 'line') : tr('pharmacy', 'lines_2')}.
      </p>
      {perMode.length > 0 ? (
        <div className="mt-1">
          <p>{tr('pharmacy', 'paid_back_to_the_customer')} {money(outcome.refundedMinor)}</p>
          <ul className="mt-0.5 list-disc ps-5">
            {perMode.map((entry) => (
              <li key={entry.mode}>
                {humanize(entry.mode)}: {money(entry.amountMinor)}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mt-1">{tr('pharmacy', 'no_money_had_to_be_paid_back_for_this_return')}</p>
      )}
    </Alert>
  );
}

function SupervisorApprovalDialog({
  body,
  creditMinor,
  onClose,
  onReturned,
}: {
  body: ReturnBody;
  creditMinor: number;
  onClose: () => void;
  onReturned: (outcome: ReturnOutcome) => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const approve = useMutation({
    gcTime: 0,
    mutationFn: () =>
      post<ReturnOutcome>('/pharmacy/sales/return', body, {
        idempotencyKey,
        headers: supervisorHeaders({ username: username.trim(), password, ...(totpCode.trim() ? { totpCode: totpCode.trim() } : {}) }),
      }),
    onSuccess: (outcome) => {
      setPassword('');
      onReturned(outcome);
    },
    onError: (caught) => {
      setPassword('');
      setError(describeError(caught));
    },
  });

  const ready = username.trim().length >= 2 && password.length > 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready && !approve.isPending) approve.mutate();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('pharmacy', 'supervisor_approval_needed')}
      description={tr('pharmacy', 'this_return_about_x_is_above_the_counter_limit', { creditMinor: money(creditMinor) })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={approve.isPending}>
            {tr('pharmacy', 'cancel')}
          </Button>
          <Button type="submit" form="supervisor-approval" loading={approve.isPending} disabled={!ready}>
            {tr('pharmacy', 'approve_and_return')}
          </Button>
        </>
      }
    >
      <form id="supervisor-approval" onSubmit={submit} className="space-y-4">
        <Field label={tr('pharmacy', 'supervisor_username')} required>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
            disabled={approve.isPending}
          />
        </Field>
        <Field label={tr('pharmacy', 'supervisor_password')} required>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
            disabled={approve.isPending}
          />
        </Field>
        <Field label={tr('pharmacy', 'authenticator_code')} hint={tr('pharmacy', 'only_if_the_supervisor_uses_two_factor_sign_in')}>
          <Input
            inputMode="numeric"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value)}
            autoComplete="one-time-code"
            disabled={approve.isPending}
          />
        </Field>
        <p className="text-xs text-muted-foreground">
          {tr('pharmacy', 'the_supervisor_must_be_a_different_person_from')}
        </p>
        {error && <Alert variant="destructive">{error}</Alert>}
      </form>
    </Dialog>
  );
}

function ReturnForm({ sale }: { sale: Sale }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [reason, setReason] = useState<ReturnReason>('wrong_item');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ReturnOutcome | null>(null);
  const [approving, setApproving] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());

  const blocked = blockedReason(sale);
  const restocked = RETURN_REASONS.find((option) => option.value === reason)?.restocked ?? false;

  const requested = sale.items
    .map((line) => ({ line, qty: parseQuantity(quantities[line.id]) }))
    .filter((entry) => entry.qty > 0);
  const hasProblem = sale.items.some((line) => quantityProblem(line, quantities[line.id]) !== null);
  const creditEstimateMinor = requested.reduce((sum, entry) => sum + creditFor(entry.line, entry.qty), 0);

  const body: ReturnBody = {
    invoiceId: sale.id,
    lines: requested.map((entry) => ({ invoiceItemId: entry.line.id, qty: entry.qty })),
    reason,
    ...(notes.trim() ? { notes: notes.trim() } : {}),
  };

  const setQuantity = (lineId: string, text: string) => setQuantities((current) => ({ ...current, [lineId]: text }));

  const returnEverything = () =>
    setQuantities(
      Object.fromEntries(
        sale.items
          .filter((line) => returnableQty(line) > 0)
          .map((line): [string, string] => [line.id, formatQuantity(returnableQty(line))]),
      ),
    );

  const finish = (result: ReturnOutcome) => {
    setOutcome(result);
    setQuantities({});
    setNotes('');
    setError(null);
    setApproving(false);
    setIdempotencyKey(newIdempotencyKey());
    toast.success(tr('pharmacy', 'return_recorded_2'), tr('pharmacy', 'credit_x', { creditMinor: money(result.creditMinor) }));
    void queryClient.invalidateQueries({ queryKey: ['pharmacy-return-sale', sale.id] });
    void queryClient.invalidateQueries({ queryKey: ['pharmacy-return-search'] });
    void queryClient.invalidateQueries({ queryKey: ['stock'] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };

  const send = useMutation({
    mutationFn: () => post<ReturnOutcome>('/pharmacy/sales/return', body, { idempotencyKey }),
    onSuccess: finish,
    onError: (caught) => {
      if (needsSupervisor(caught)) {
        setError(null);
        setApproving(true);
        return;
      }
      setError(describeError(caught));
    },
  });

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between">
        <div>
          <CardTitle className="text-sm">{tr('pharmacy', 'sale')} {sale.number}</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {customerOf(sale)} · {dateTime(sale.createdAt)}
          </p>
        </div>
        <StatusBadge status={sale.status} />
      </CardHeader>
      <CardContent className="space-y-4">
        {outcome && <OutcomeSummary outcome={outcome} />}

        {blocked ? (
          <Alert variant="warning">{blocked}</Alert>
        ) : (
          <>
            <DataTable
              dense
              rows={sale.items}
              rowKey={(line) => line.id}
              empty={tr('pharmacy', 'this_sale_has_no_lines')}
              columns={[
                { header: tr('pharmacy', 'item'), cell: (line) => line.description },
                { header: tr('pharmacy', 'sold'), align: 'right', cell: (line) => formatQuantity(line.qty) },
                {
                  header: tr('pharmacy', 'already_returned'),
                  align: 'right',
                  cell: (line) => (line.returnedQty > 0 ? formatQuantity(line.returnedQty) : '—'),
                },
                { header: tr('pharmacy', 'price'), align: 'right', cell: (line) => money(line.unitPriceMinor) },
                { header: tr('pharmacy', 'line_total'), align: 'right', cell: (line) => money(line.netMinor) },
                {
                  header: tr('pharmacy', 'return_quantity'),
                  align: 'right',
                  cell: (line) => {
                    const room = returnableQty(line);
                    const problem = quantityProblem(line, quantities[line.id]);
                    if (room === 0) return <span className="text-xs text-muted-foreground">{tr('pharmacy', 'nothing_left')}</span>;
                    return (
                      <div className="ms-auto w-32 space-y-1 text-start">
                        <Input
                          type="number"
                          min={0}
                          max={room}
                          step="any"
                          className="tabular"
                          placeholder={tr('pharmacy', '0_to_x', { room: formatQuantity(room) })}
                          aria-label={tr('pharmacy', 'quantity_of_x_to_return', { description: line.description })}
                          aria-invalid={problem !== null}
                          value={quantities[line.id] ?? ''}
                          disabled={send.isPending}
                          onChange={(e) => setQuantity(line.id, e.target.value)}
                        />
                        {problem && <p className="text-xs font-medium text-destructive">{problem}</p>}
                      </div>
                    );
                  },
                },
              ]}
            />

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={returnEverything} disabled={send.isPending}>
                {tr('pharmacy', 'return_everything_that_is_left')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQuantities({})}
                disabled={send.isPending || requested.length === 0}
              >
                {tr('pharmacy', 'clear')}
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Field
                label={tr('pharmacy', 'reason_for_the_return')}
                hint={
                  restocked
                    ? tr('pharmacy', 'these_goods_go_back_on_the_shelf_and_can_be')
                    : tr('pharmacy', 'these_goods_are_written_off_not_restocked_only')
                }
              >
                <ChoiceButtons
                  layout="chips"
                  value={reason}
                  onChange={setReason}
                  disabled={send.isPending}
                  options={RETURN_REASONS.map((option) => ({ value: option.value, label: option.label }))}
                />
              </Field>
              <Field label={tr('pharmacy', 'notes_optional')}>
                <Textarea
                  maxLength={NOTES_MAX_LENGTH}
                  value={notes}
                  disabled={send.isPending}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Field>
            </div>

            {error && <Alert variant="destructive">{error}</Alert>}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {requested.length === 0
                  ? tr('pharmacy', 'enter_a_quantity_on_at_least_one_line')
                  : `Credit of about ${money(creditEstimateMinor)} for ${requested.length} ${requested.length === 1 ? 'line' : 'lines'}.`}
              </p>
              <Button
                onClick={() => send.mutate()}
                loading={send.isPending}
                disabled={requested.length === 0 || hasProblem}
              >
                {tr('pharmacy', 'return_items')}
              </Button>
            </div>
          </>
        )}
      </CardContent>

      {approving && (
        <SupervisorApprovalDialog
          body={body}
          creditMinor={creditEstimateMinor}
          onClose={() => setApproving(false)}
          onReturned={finish}
        />
      )}
    </Card>
  );
}

export function SalesReturnPanel() {
  const [searched, setSearched] = useState('');
  const [cursors, setCursors] = useState<string[]>([]);
  const [saleId, setSaleId] = useState<string | null>(null);

  const cursor = cursors[cursors.length - 1];
  const search = qs({ q: searched, context: 'pharmacy', limit: SEARCH_PAGE_SIZE, cursor });

  const results = useQuery({
    queryKey: ['pharmacy-return-search', search],
    queryFn: () => get<SaleSearchPage>(`/invoices${search}`),
    enabled: searched.length >= MIN_SEARCH_LENGTH,
  });

  const sale = useQuery({
    queryKey: ['pharmacy-return-sale', saleId],
    queryFn: () => get<Sale>(`/invoices/${saleId}`),
    enabled: saleId !== null,
  });

  const nextCursor = results.data?.nextCursor ?? null;

  const submitSearch = (value: string) => {
    setSearched(value);
    setCursors([]);
    setSaleId(null);
  };

  return (
    <div className="space-y-4">
      <SearchFilterBar
        className="mb-0"
        search={{
          value: searched,
          onChange: submitSearch,
          label: tr('pharmacy', 'find_the_sale'),
          placeholder: tr('pharmacy', 'the_invoice_number_from_the_receipt_or_the'),
          autoFocus: true,
        }}
        onReset={searched ? () => submitSearch('') : undefined}
      />

      {searched === '' ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          {tr('pharmacy', 'search_for_the_sale_the_goods_came_from_its')}
        </div>
      ) : (
        <DataTable
          rows={results.data?.data}
          loading={results.isLoading} refreshing={results.isFetching}
          error={results.error}
          rowKey={(row) => row.id}
          onRowClick={(row) => setSaleId(row.id)}
          empty={tr('pharmacy', 'no_pharmacy_sale_matches_that_check_the_invoice')}
          footer={
            cursors.length > 0 || nextCursor ? (
              <div className="flex w-full justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={cursors.length === 0 || results.isFetching}
                  onClick={() => setCursors((current) => current.slice(0, -1))}
                >
                  {tr('pharmacy', 'previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!nextCursor || results.isFetching}
                  onClick={() => nextCursor && setCursors((current) => [...current, nextCursor])}
                >
                  {tr('pharmacy', 'next')}
                </Button>
              </div>
            ) : undefined
          }
          columns={[
            { header: tr('pharmacy', 'invoice'), sort: (row) => row.number, cell: (row) => <span className="tabular font-medium">{row.number}</span> },
            { header: tr('pharmacy', 'customer'), sort: (row) => customerOf(row), cell: (row) => customerOf(row) },
            { header: tr('pharmacy', 'date'), sort: (row) => row.createdAt, cell: (row) => dateTime(row.createdAt) },
            { header: tr('pharmacy', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
            { header: tr('pharmacy', 'total'), align: 'right', sort: (row) => row.totals.netMinor, cell: (row) => money(row.totals.netMinor) },
            {
              header: '',
              align: 'right',
              cell: (row) => (
                <Button
                  size="sm"
                  variant={row.id === saleId ? 'default' : 'outline'}
                  onClick={() => setSaleId(row.id)}
                >
                  {row.id === saleId ? tr('pharmacy', 'selected') : tr('pharmacy', 'return_items')}
                </Button>
              ),
            },
          ]}
        />
      )}

      {saleId && sale.isLoading && (
        <div className="rounded-lg border p-10 text-center text-sm text-muted-foreground">{tr('pharmacy', 'loading_the_sale')}</div>
      )}
      {saleId && sale.error && <Alert variant="destructive">{describeError(sale.error)}</Alert>}
      {saleId && sale.data && <ReturnForm key={sale.data.id} sale={sale.data} />}
    </div>
  );
}
